import { FieldArray } from 'react-final-form-arrays';
import { Field, FormSpy } from 'react-final-form';

import { ShowMoreButton } from 'UIPackage/MainModule/components/ShowMoreButton/ShowMoreButton';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { Button } from 'UIPackage/MainModule/components/Button';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { getFieldArrayErrorMessage } from 'CorePackage/MainModule/common/getFieldArrayErrorMessage';
import { IKnmErknmFormViolatedRequirementsNpa } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormViolatedRequirementsNpa';
import { KnmViolatedRequirementsNpaItem } from 'KnmPackage/KnmViolatedRequirementsNpa/components/KnmViolatedRequirementsNpaItem';
import { IDecisionFormRef } from 'DecisionPackage/MainModule/interfaces/IDecisionFormRef';
import { getTitleFieldWithRedStar } from 'functions/getTitleFieldWithRedStar';

import styles from './ResultDecisionsItemContentViolations.module.scss';
import { useShowMore } from '../../../../../../hooks/useShowMore';

interface IResultDecisionsItemContentViolationsProps {
  name: string;
  watchOnly: boolean;
  formRef: IKnmFormRef | IDecisionFormRef | undefined;
  decisionIndex?: number;
  decisionPmIndex?: number;
  addRequirementsNpa: (decisionIndex: number, fieldName: string) => void;
  actsRequirementsNpa: IKnmErknmFormViolatedRequirementsNpa[];
  isPaperless: boolean;
  onChangeFixPlanDate?: (date: string | Date) => void;
  id: string;
  isKnm?: boolean;
  isPm?: boolean;
  clearWatchOnly?: boolean;
  signed?: boolean;
  isExpandAllInPrescription?: boolean;
  extractVersion?: number;
}

export const ResultDecisionsItemContentViolations = ({
  addRequirementsNpa,
  formRef,
  decisionIndex,
  watchOnly,
  name,
  actsRequirementsNpa,
  isPaperless,
  decisionPmIndex,
  onChangeFixPlanDate,
  id,
  isKnm,
  isPm,
  clearWatchOnly,
  signed,
  isExpandAllInPrescription,
  extractVersion,
}: IResultDecisionsItemContentViolationsProps) => {
  const remove = formRef?.current.mutators.remove;

  const violatedRequirementsNpaFieldName = `${name}.violatedMandatoryRequirements`;

  const removeViolatedRequirementsNpa = (npaIndex: number) => {
    remove && remove(violatedRequirementsNpaFieldName, npaIndex);
  };

  return (
    <div className={styles.GroupMarginBottom}>
      <FieldBlock
        label={getTitleFieldWithRedStar({
          label: 'Выявленные нарушения обязательных требований',
          field: violatedRequirementsNpaFieldName,
          isKnm,
          isPm,
        })}
        id={`${id}.violatedMandatoryRequirements`}
        button={
          <Button
            className="shared-btn-add"
            text="Добавить"
            type="button"
            variant="link"
            color="primary"
            disabled={watchOnly}
            onClick={() => addRequirementsNpa(decisionIndex || decisionPmIndex || 0, violatedRequirementsNpaFieldName)}
          />
        }
      />
      <FormSpy subscription={{ errors: true, touched: true }}>
        {props => {
          let fieldError: string | undefined;
          let isFieldTouched = false;

          if (decisionIndex !== undefined) {
            fieldError = getFieldArrayErrorMessage(
              props.errors?.resultDecisions?.[decisionIndex]?.violatedMandatoryRequirements,
            );
            isFieldTouched = !!props.touched?.places;
          }
          if (decisionPmIndex !== undefined) {
            fieldError = getFieldArrayErrorMessage(
              props.errors?.pmResultDecisions?.[decisionPmIndex]?.violatedMandatoryRequirements,
            );
            isFieldTouched = !!props.touched?.[`pmResultDecisions[${decisionPmIndex}].violatedMandatoryRequirements`];
          }

          if (isFieldTouched && fieldError) {
            return <p className="shared-input-error shared-row__small-margin">{fieldError}</p>;
          }

          return null;
        }}
      </FormSpy>
      <FieldArray name={violatedRequirementsNpaFieldName}>
        {({ fields }) => {
          const visibleFieldsValues = fields.value?.filter((fieldValue: IKnmErknmFormViolatedRequirementsNpa) => {
            const correspondingActRequirement = actsRequirementsNpa.find(
              requirement => requirement.npa.id === fieldValue.npa.id,
            );

            return !!correspondingActRequirement;
          });

          const { visibleItems, hasMore, showMore, totalCount, visibleCount } = useShowMore(visibleFieldsValues, {
            initialCount: 10,
            incrementCount: 10,
          });

          return (
            <>
              {visibleItems.map((fieldValue: IKnmErknmFormViolatedRequirementsNpa) => {
                const npaIndex =
                  fields.value?.findIndex((item: IKnmErknmFormViolatedRequirementsNpa) => item === fieldValue) ?? 0;
                const npaName = `${violatedRequirementsNpaFieldName}[${npaIndex}]`;
                const correspondingActRequirement = actsRequirementsNpa.find(
                  requirement =>
                    requirement.npa.id === fieldValue.npa.id &&
                    requirement.structuralUnit.id === fieldValue.structuralUnit.id,
                );

                if (!correspondingActRequirement) {
                  return null;
                }

                return (
                  <Field key={npaName} name={npaName}>
                    {({ meta }) => {
                      const isFieldTouched = meta.touched;
                      const errorField = meta.error;

                      return (
                        <KnmViolatedRequirementsNpaItem
                          value={fieldValue}
                          name={npaName}
                          watchOnly={watchOnly}
                          isViolationFieldsWatchOnly={!!clearWatchOnly}
                          onRemove={() => removeViolatedRequirementsNpa(npaIndex)}
                          addMandatory={() => undefined}
                          touched={isFieldTouched}
                          error={errorField}
                          formRef={formRef}
                          isMandatoryDisabled={true}
                          isInsideDecisionsSection
                          actRequirement={correspondingActRequirement}
                          isPaperless={isPaperless}
                          onChangeFixPlanDate={onChangeFixPlanDate}
                          id={`${id}.violatedMandatoryRequirements[${fieldValue.guid ? fieldValue.guid : npaIndex}]`}
                          isKnm={isKnm}
                          isPm={isPm}
                          signed={signed}
                          isExpandAll={isExpandAllInPrescription}
                          extractVersion={extractVersion}
                        />
                      );
                    }}
                  </Field>
                );
              })}
              {hasMore && <ShowMoreButton onClick={showMore} totalCount={totalCount} visibleCount={visibleCount} />}
            </>
          );
        }}
      </FieldArray>
    </div>
  );
};
